/* ==========================================================================
   Cart Boost — front-end logic for cart drawer enhancements.
   Currently powers: <m-cart-rewards> (multi-tier rewards / free-shipping bar).
   Self-updating: subscribes to the theme's `cart-update` event and re-reads
   the cart total, so it stays in sync when quantities change or items are added.
   ========================================================================== */
(function () {
  'use strict';

  /* --- Money formatting (Shopify standard, reads the theme money format) --- */
  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    var value = '';
    var fmt = format || (window.MinimogSettings && window.MinimogSettings.money_format) || '${{amount}}';
    var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    var match = fmt.match(placeholderRegex);
    if (!match) return fmt; // format string has no {{placeholder}} — nothing to substitute

    function defaultTo(num, precision, thousands, decimal) {
      thousands = thousands === undefined ? ',' : thousands;
      decimal = decimal === undefined ? '.' : decimal;
      if (isNaN(num) || num == null) return 0;
      num = (num / 100.0).toFixed(precision);
      var parts = num.split('.');
      var dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      var centsPart = parts[1] ? decimal + parts[1] : '';
      return dollars + centsPart;
    }

    switch (match[1]) {
      case 'amount': value = defaultTo(cents, 2); break;
      case 'amount_no_decimals': value = defaultTo(cents, 0); break;
      case 'amount_with_comma_separator': value = defaultTo(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = defaultTo(cents, 0, '.', ','); break;
      case 'amount_with_space_separator': value = defaultTo(cents, 2, ' ', ','); break;
      case 'amount_no_decimals_with_space_separator': value = defaultTo(cents, 0, ' ', ''); break;
      case 'amount_with_apostrophe_separator': value = defaultTo(cents, 2, "'", '.'); break;
      default: value = defaultTo(cents, 2);
    }
    return fmt.replace(placeholderRegex, value);
  }

  /* --- Icon set (inline SVG, currentColor) --- */
  var ICONS = {
    truck: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M1 3h13v10H1zM14 7h4l3 3v3h-7"/><circle cx="5.5" cy="16.5" r="1.8"/><circle cx="16.5" cy="16.5" r="1.8"/></svg>',
    gift: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 12 20 22 4 22 4 12"/><rect x="2" y="7" width="20" height="5"/><line x1="12" y1="22" x2="12" y2="7"/><path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z"/><path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z"/></svg>',
    home: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M3 9.5 12 3l9 6.5V21H3z"/><path d="M9 21v-6h6v6"/></svg>',
    car: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M5 13l1.5-4.5A2 2 0 0 1 8.4 7h7.2a2 2 0 0 1 1.9 1.5L19 13v5h-2v-2H7v2H5z"/><circle cx="7.5" cy="16" r="1.3"/><circle cx="16.5" cy="16" r="1.3"/></svg>',
    star: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><polygon points="12 2 15 9 22 9.5 17 14 18.5 21 12 17.5 5.5 21 7 14 2 9.5 9 9"/></svg>',
    percent: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><line x1="19" y1="5" x2="5" y2="19"/><circle cx="7" cy="7" r="2.2"/><circle cx="17" cy="17" r="2.2"/></svg>'
  };

  class CartRewards extends HTMLElement {
    constructor() {
      super();
      this._onCartUpdate = this._onCartUpdate.bind(this);
    }

    connectedCallback() {
      try {
        this.tiers = JSON.parse(this.dataset.config || '[]');
      } catch (e) {
        this.tiers = [];
      }
      this.tiers.sort(function (a, b) { return a.amount - b.amount; });
      this.moneyFormat = this.dataset.moneyFormat || undefined;
      this.intro = this.dataset.intro || 'You are [amount] away from [goal]!';
      this.success = this.dataset.success || '';
      this.total = parseInt(this.dataset.total, 10) || 0;

      if (!this.tiers.length) return;
      this.render();

      if (window.MinimogEvents && typeof window.MinimogEvents.subscribe === 'function') {
        this._unsub = window.MinimogEvents.subscribe('cart-update', this._onCartUpdate);
      }
    }

    disconnectedCallback() {
      if (this._unsub) this._unsub();
    }

    _onCartUpdate(payload) {
      var cart = payload && (payload.cart || payload);
      if (cart && cart.items) {
        this.total = nonGiftTotal(cart);
        this.render();
      } else {
        this.refresh();
      }
    }

    refresh() {
      var self = this;
      fetch((window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/') + 'cart.js')
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          self.total = nonGiftTotal(cart);
          self.render();
        })
        .catch(function () {});
    }

    _money(cents) { return formatMoney(cents, this.moneyFormat); }

    render() {
      var tiers = this.tiers;
      var n = tiers.length;
      var total = this.total;

      // milestone centers, evenly spaced
      var positions = tiers.map(function (t, i) { return ((i + 0.5) / n) * 100; });

      // fill position mapped so it reaches each icon exactly at its threshold
      var fill = 0;
      if (total > 0) {
        var prevPos = 0, prevAmt = 0, done = false;
        for (var i = 0; i < n; i++) {
          if (total < tiers[i].amount) {
            var frac = (total - prevAmt) / (tiers[i].amount - prevAmt);
            fill = prevPos + (positions[i] - prevPos) * frac;
            done = true;
            break;
          }
          prevAmt = tiers[i].amount;
          prevPos = positions[i];
        }
        if (!done) fill = 100;
      }

      // next unreached tier
      var next = null;
      for (var j = 0; j < n; j++) {
        if (total < tiers[j].amount) { next = tiers[j]; break; }
      }

      var msg;
      if (next) {
        var remaining = next.amount - total;
        msg = this.intro
          .replace('[amount]', '<strong>' + this._money(remaining) + '</strong>')
          .replace('[goal]', '<strong>' + this._escape(next.label) + '</strong>');
        this.classList.remove('is-complete');
      } else {
        msg = this.success || '';
        this.classList.add('is-complete');
      }

      var milestones = tiers.map(function (t, i) {
        var reached = total >= t.amount;
        var icon = ICONS[t.icon] || ICONS.gift;
        return (
          '<div class="cart-rewards__milestone' + (reached ? ' is-reached' : '') + '">' +
            '<span class="cart-rewards__icon">' + icon + '</span>' +
            '<span class="cart-rewards__amount">' + this._money(t.amount) + '</span>' +
            '<span class="cart-rewards__label">' + this._escape(t.label) + '</span>' +
          '</div>'
        );
      }, this).join('');

      this.innerHTML =
        (msg ? '<p class="cart-rewards__message">' + msg + '</p>' : '') +
        '<div class="cart-rewards__track">' +
          '<span class="cart-rewards__track-bg"></span>' +
          '<span class="cart-rewards__track-fill" style="width:' + fill + '%"></span>' +
          '<div class="cart-rewards__milestones">' + milestones + '</div>' +
        '</div>';
    }

    _escape(str) {
      return String(str == null ? '' : str)
        .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    }
  }

  if (!customElements.get('m-cart-rewards')) {
    customElements.define('m-cart-rewards', CartRewards);
  }

  /* ====================================================================
     Shared cart helpers (reuse the theme's routes + drawer refresh)
     ==================================================================== */
  function routes() {
    return (window.MinimogSettings && window.MinimogSettings.routes) || {};
  }
  function root() {
    return routes().root || (window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/');
  }
  function postJSON(url, data) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(data)
    }).then(function (r) { return r.json(); });
  }
  function getCart() {
    return fetch(root() + 'cart.js', { headers: { Accept: 'application/json' } }).then(function (r) { return r.json(); });
  }
  /* Cart total EXCLUDING auto-added free gifts. Automatic (100%-off) discounts
     are not reflected in the AJAX cart total, so raw total_price over-counts a
     gift line. Progress/summary must use this instead of cart.total_price. */
  function nonGiftTotal(cart) {
    if (!cart || !cart.items) return cart && typeof cart.total_price === 'number' ? cart.total_price : 0;
    var sum = 0;
    cart.items.forEach(function (it) {
      if (!(it.properties && it.properties._cb_gift)) sum += it.final_line_price;
    });
    return sum;
  }
  /* Debounce the native section re-render so overlapping refreshes (e.g. a gift
     auto-add firing while a previous refresh is in flight) coalesce — the last
     fetch wins, avoiding a stale subtotal/discount clobbering the fresh one. */
  var _nativeRefreshTimer = null;
  function scheduleNativeRefresh() {
    if (_nativeRefreshTimer) clearTimeout(_nativeRefreshTimer);
    _nativeRefreshTimer = setTimeout(function () {
      var drawer = document.querySelector('m-cart-drawer');
      if (drawer && typeof drawer.onCartDrawerUpdate === 'function') drawer.onCartDrawerUpdate();
    }, 120);
  }
  function refreshDrawer(cart) {
    var drawer = document.querySelector('m-cart-drawer');
    if (drawer && cart) drawer.classList.toggle('m-cart--empty', cart.item_count === 0);
    scheduleNativeRefresh();
    if (window.MinimogEvents && typeof window.MinimogEvents.emit === 'function') {
      window.MinimogEvents.emit('cart-update', cart);
    }
  }

  /* ====================================================================
     Upsell carousel — "Add" buttons
     ==================================================================== */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-upsell-add]');
    if (!btn) return;
    e.preventDefault();
    if (btn.classList.contains('is-loading')) return;
    var id = parseInt(btn.dataset.variantId, 10);
    if (!id) return;
    btn.classList.add('is-loading');
    postJSON(routes().cart_add_url || '/cart/add.js', { id: id, quantity: 1 })
      .then(function () { return getCart(); })
      .then(function (cart) {
        btn.classList.remove('is-loading');
        btn.classList.add('is-in-cart');
        refreshDrawer(cart);
      })
      .catch(function () { btn.classList.remove('is-loading'); });
  });

  /* Keep upsell "Add" buttons in sync with the cart: mark green only while the
     variant is actually in the cart, and revert when it is removed. */
  function syncUpsellButtons(cart) {
    var inCart = {};
    ((cart && cart.items) || []).forEach(function (it) { inCart[it.variant_id] = true; });
    document.querySelectorAll('[data-upsell-add]').forEach(function (btn) {
      var id = parseInt(btn.dataset.variantId, 10);
      if (!btn.classList.contains('is-loading')) {
        btn.classList.toggle('is-in-cart', !!inCart[id]);
      }
    });
  }
  if (window.MinimogEvents && typeof window.MinimogEvents.subscribe === 'function') {
    window.MinimogEvents.subscribe('cart-update', function (payload) {
      var cart = payload && (payload.cart || payload);
      if (cart && cart.items) syncUpsellButtons(cart);
      else getCart().then(syncUpsellButtons).catch(function () {});
    });
  }

  /* ====================================================================
     Toggle add-on (priority shipping) — checkbox adds/removes a variant
     ==================================================================== */
  class CartToggleAddon extends HTMLElement {
    connectedCallback() {
      this.variant = parseInt(this.dataset.variantId, 10);
      this.checkbox = this.querySelector('[data-priority-toggle]');
      if (this.checkbox) this.checkbox.addEventListener('change', this.onToggle.bind(this));
      this._onCartUpdate = this._onCartUpdate.bind(this);
      if (window.MinimogEvents && typeof window.MinimogEvents.subscribe === 'function') {
        this._unsub = window.MinimogEvents.subscribe('cart-update', this._onCartUpdate);
      }
    }
    disconnectedCallback() { if (this._unsub) this._unsub(); }
    _onCartUpdate(payload) {
      // Keep the checkbox in sync if the add-on line is removed via another path.
      if (!this.checkbox || this.classList.contains('is-loading')) return;
      var cart = payload && (payload.cart || payload);
      var self = this;
      var sync = function (c) {
        var inCart = ((c && c.items) || []).some(function (it) { return it.variant_id === self.variant; });
        self.checkbox.checked = inCart;
      };
      if (cart && cart.items) sync(cart);
      else getCart().then(sync).catch(function () {});
    }
    onToggle() {
      var self = this;
      var qty = this.checkbox.checked ? 1 : 0;
      var updates = {};
      updates[this.variant] = qty;
      this.classList.add('is-loading');
      postJSON(routes().cart_update_url || '/cart/update.js', { updates: updates })
        .then(function (cart) { self.classList.remove('is-loading'); refreshDrawer(cart); })
        .catch(function () {
          self.classList.remove('is-loading');
          self.checkbox.checked = !self.checkbox.checked;
        });
    }
  }
  if (!customElements.get('m-cart-toggle-addon')) {
    customElements.define('m-cart-toggle-addon', CartToggleAddon);
  }

  /* ====================================================================
     Free-gift cards — progress rings + auto add/remove at thresholds
     Gift lines are tagged with property `_cb_gift` so the qualifying total
     never counts the gift itself (prevents oscillation).
     ==================================================================== */
  class CartGifts extends HTMLElement {
    connectedCallback() {
      try { this.tiers = JSON.parse(this.dataset.config || '[]'); } catch (e) { this.tiers = []; }
      this.lockedTpl = this.dataset.locked || '';
      this.unlockedTpl = this.dataset.unlocked || '';
      this.moneyFormat = this.dataset.moneyFormat || undefined;
      this.cards = Array.prototype.slice.call(this.querySelectorAll('[data-gift-card]'));
      this._reconciling = false;
      this._onCartUpdate = this._onCartUpdate.bind(this);
      if (window.MinimogEvents) this._unsub = window.MinimogEvents.subscribe('cart-update', this._onCartUpdate);
      this.refresh();
    }
    disconnectedCallback() { if (this._unsub) this._unsub(); }
    _onCartUpdate(payload) {
      var cart = payload && (payload.cart || payload);
      if (cart && cart.items) this.apply(cart);
      else this.refresh();
    }
    refresh() {
      var self = this;
      getCart().then(function (c) { self.apply(c); }).catch(function () {});
    }
    qualifyingTotal(cart) {
      var sum = 0;
      (cart.items || []).forEach(function (it) {
        var isGift = it.properties && it.properties._cb_gift;
        if (!isGift) sum += it.final_line_price;
      });
      return sum;
    }
    apply(cart) {
      var qualifying = this.qualifyingTotal(cart);
      this.updateUI(qualifying);
      this.reconcile(cart, qualifying);
    }
    updateUI(qualifying) {
      var self = this;
      this.cards.forEach(function (card) {
        var threshold = parseInt(card.dataset.threshold, 10);
        var reached = qualifying >= threshold;
        var pct = threshold ? Math.max(0, Math.min(1, qualifying / threshold)) : 1;
        card.classList.toggle('is-unlocked', reached);
        var fg = card.querySelector('[data-gift-ring-fg]');
        if (fg) {
          var C = 2 * Math.PI * 19;
          fg.style.strokeDasharray = C;
          fg.style.strokeDashoffset = C * (1 - pct);
        }
        var status = card.querySelector('[data-gift-status]');
        if (status) {
          if (reached) {
            status.textContent = self.unlockedTpl;
          } else {
            var remaining = threshold - qualifying;
            status.textContent = self.lockedTpl.replace('[amount]', formatMoney(remaining, self.moneyFormat));
          }
        }
      });
    }
    reconcile(cart, qualifying) {
      if (this._reconciling) return;
      // Key gift lines by their tier index (_cb_gift), not the bare variant, so
      // two tiers that point at the same product each own their own line.
      var giftLines = {};
      (cart.items || []).forEach(function (it) {
        if (it.properties && it.properties._cb_gift) giftLines[it.properties._cb_gift] = it;
      });
      for (var k = 0; k < this.tiers.length; k++) {
        var t = this.tiers[k];
        if (!t.amount || t.amount <= 0) continue; // never auto-add a zero-threshold gift
        var key = String(t.i);
        var present = !!giftLines[key];
        var desired = qualifying >= t.amount;
        if (desired && !present) { this._add(t); return; }
        if (!desired && present) { this._remove(giftLines[key]); return; }
      }
    }
    _add(t) {
      this._reconciling = true;
      var self = this;
      postJSON(routes().cart_add_url || '/cart/add.js', {
        id: t.variant, quantity: 1, properties: { _cb_gift: String(t.i) }
      })
        .then(function () { return getCart(); })
        .then(function (cart) { self._reconciling = false; refreshDrawer(cart); })
        .catch(function () { self._reconciling = false; });
    }
    _remove(line) {
      this._reconciling = true;
      var self = this;
      postJSON(routes().cart_change_url || '/cart/change.js', { id: line.key, quantity: 0 })
        .then(function (cart) { self._reconciling = false; refreshDrawer(cart); })
        .catch(function () { self._reconciling = false; });
    }
  }
  if (!customElements.get('m-cart-gifts')) {
    customElements.define('m-cart-gifts', CartGifts);
  }

  /* ====================================================================
     Order summary — self-updating
     ==================================================================== */
  class CartSummary extends HTMLElement {
    connectedCallback() {
      this.moneyFormat = this.dataset.moneyFormat || undefined;
      this.flat = parseInt(this.dataset.flatShipping, 10) || 0;
      this.freeship = parseInt(this.dataset.freeship, 10) || 0;
      this.total = parseInt(this.dataset.total, 10) || 0;
      this.labels = {
        value: this.dataset.valueLabel || '',
        shipping: this.dataset.shippingLabel || '',
        total: this.dataset.totalLabel || '',
        free: this.dataset.freeLabel || '',
        note: this.dataset.checkoutNote || ''
      };
      this.rowsEl = this.querySelector('[data-summary-rows]');
      this._onCartUpdate = this._onCartUpdate.bind(this);
      if (window.MinimogEvents) this._unsub = window.MinimogEvents.subscribe('cart-update', this._onCartUpdate);
      this.render();
    }
    disconnectedCallback() { if (this._unsub) this._unsub(); }
    _onCartUpdate(payload) {
      var cart = payload && (payload.cart || payload);
      if (cart && cart.items) { this.total = nonGiftTotal(cart); this.render(); }
      else { var self = this; getCart().then(function (c) { self.total = nonGiftTotal(c); self.render(); }).catch(function () {}); }
    }
    _money(cents) { return formatMoney(cents, this.moneyFormat); }
    _esc(s) { return String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;'); }
    _row(label, valueHtml, strong) {
      return '<div class="cart-summary__row' + (strong ? ' cart-summary__row--total' : '') + '">' +
        '<span>' + this._esc(label) + '</span><span>' + valueHtml + '</span></div>';
    }
    render() {
      if (!this.rowsEl) return;
      var total = this.total;
      var shippingHtml;
      if (this.freeship && total >= this.freeship) {
        shippingHtml = '<span class="cart-summary__free">' + this._esc(this.labels.free) + '</span>';
      } else if (this.flat > 0) {
        shippingHtml = this._money(this.flat);
        total = total + this.flat;
      } else {
        shippingHtml = '<span class="cart-summary__note">' + this._esc(this.labels.note) + '</span>';
      }
      this.rowsEl.innerHTML =
        this._row(this.labels.value, this._money(this.total)) +
        this._row(this.labels.shipping, shippingHtml) +
        this._row(this.labels.total, this._money(total), true);
    }
  }
  if (!customElements.get('m-cart-summary')) {
    customElements.define('m-cart-summary', CartSummary);
  }

  /* ====================================================================
     Delivery estimate — fills "[label] Weekday (DD.MM) – Weekday (DD.MM)"
     from a business-day lead time, localized to the shop locale.
     ==================================================================== */
  function fillDeliveryEstimate() {
    var els = document.querySelectorAll('[data-cb-delivery]');
    if (!els.length) return;
    var locale = (window.MinimogSettings && window.MinimogSettings.shop_locale && window.MinimogSettings.shop_locale.current) || undefined;
    function addBusinessDays(base, days) {
      var d = new Date(base.getTime());
      var added = 0;
      while (added < days) {
        d.setDate(d.getDate() + 1);
        var wd = d.getDay();
        if (wd !== 0 && wd !== 6) added++;
      }
      return d;
    }
    function fmt(d) {
      var wd;
      try { wd = d.toLocaleDateString(locale, { weekday: 'short' }); }
      catch (e) { wd = d.toLocaleDateString(undefined, { weekday: 'short' }); }
      wd = wd.replace(/\.$/, '');
      wd = wd.charAt(0).toUpperCase() + wd.slice(1);
      var dd = ('0' + d.getDate()).slice(-2);
      var mm = ('0' + (d.getMonth() + 1)).slice(-2);
      return wd + '. (' + dd + '.' + mm + ')';
    }
    var today = new Date();
    els.forEach(function (el) {
      var min = parseInt(el.dataset.min, 10);
      var max = parseInt(el.dataset.max, 10);
      if (isNaN(min)) min = 1;
      if (isNaN(max) || max < min) max = min;
      var start = addBusinessDays(today, min);
      var end = addBusinessDays(today, max);
      var range = (min === max) ? fmt(start) : (fmt(start) + ' – ' + fmt(end));
      var label = el.dataset.label || '';
      el.textContent = (label ? label + ' ' : '') + range;
    });
  }
  /* ====================================================================
     Upsell carousel scroll affordance — nudging arrow that scrolls the row
     and hides once the end is reached (or if it doesn't overflow).
     ==================================================================== */
  function initUpsellNav() {
    document.querySelectorAll('[data-cart-upsell]').forEach(function (up) {
      var track = up.querySelector('.cart-upsell__track');
      var viewport = up.querySelector('.cart-upsell__viewport');
      var nav = up.querySelector('[data-upsell-nav]');
      if (!track || !viewport) return;
      function update() {
        var atEnd = track.scrollLeft + track.clientWidth >= track.scrollWidth - 4;
        var noOverflow = track.scrollWidth <= track.clientWidth + 4;
        viewport.classList.toggle('is-end', atEnd || noOverflow);
      }
      if (nav) {
        nav.addEventListener('click', function () {
          track.scrollBy({ left: Math.round(track.clientWidth * 0.7), behavior: 'smooth' });
        });
      }
      track.addEventListener('scroll', update, { passive: true });
      update();
    });
  }

  /* ====================================================================
     Refresh the drawer contents whenever it OPENS. External sources such as
     PageFly add a product via /cart/add.js and then open the theme drawer, but
     they don't tell it to re-render — so it would open showing stale (empty)
     contents. Observing the "open" class lets us re-sync no matter who opened it.
     ==================================================================== */
  function initDrawerRefreshOnOpen() {
    var drawer = document.querySelector('m-cart-drawer');
    if (!drawer || typeof MutationObserver === 'undefined') return;
    var wasActive = drawer.classList.contains('m-cart-drawer--active');
    var refreshing = false;
    var obs = new MutationObserver(function () {
      var isActive = drawer.classList.contains('m-cart-drawer--active');
      if (isActive && !wasActive && !refreshing) {
        refreshing = true;
        if (typeof drawer.onCartDrawerUpdate === 'function') drawer.onCartDrawerUpdate();
        getCart()
          .then(function (cart) {
            drawer.classList.toggle('m-cart--empty', cart.item_count === 0);
            if (window.MinimogEvents && window.MinimogEvents.emit) {
              window.MinimogEvents.emit('cart-update', cart);
            }
          })
          .catch(function () {})
          .then(function () { refreshing = false; });
      }
      wasActive = isActive;
    });
    obs.observe(drawer, { attributes: true, attributeFilter: ['class'] });
  }

  /* ====================================================================
     Cart count bubble + "added" bump. The theme's own updateCartCount only
     removes the `hidden` class but the bubble starts hidden via `m:hidden`, so
     the count never shows after adding to an empty cart. And external adds
     (PageFly) don't update it at all. This keeps the header count in sync on
     every cart-update and bumps the cart icon when an item is added.
     ==================================================================== */
  var lastCartCount = null;
  function currentBubbleCount() {
    var el = document.querySelector('.m-cart-count-bubble');
    var n = el ? parseInt(el.textContent, 10) : 0;
    return isNaN(n) ? 0 : n;
  }
  function updateCartBubble(count) {
    var hide = count <= 0;
    document.querySelectorAll('.m-cart-count-bubble').forEach(function (el) {
      el.textContent = count;
      el.classList.toggle('hidden', hide);
      el.classList.toggle('m:hidden', hide);
    });
  }
  function bumpCartIcon() {
    document.querySelectorAll('.m-cart-icon-bubble').forEach(function (icon) {
      icon.classList.remove('cb-cart-bump');
      void icon.offsetWidth; // reflow so the animation restarts on rapid adds
      icon.classList.add('cb-cart-bump');
    });
  }

  /* --- Fly-to-cart: a clone of the added product's image flies from the click
     point to the cart icon. Image comes from the /cart/add response; start
     point from the last pointer/click position. --- */
  var lastPointer = null;
  ['pointerdown', 'mousedown', 'touchstart', 'click'].forEach(function (evt) {
    document.addEventListener(evt, function (e) {
      var p = (e.touches && e.touches[0]) ? e.touches[0] : e;
      if (typeof p.clientX === 'number' && (p.clientX || p.clientY)) {
        lastPointer = { x: p.clientX, y: p.clientY };
      }
    }, true);
  });
  function extractAddedImage(data) {
    if (!data) return null;
    var item = (data.items && data.items.length) ? data.items[0] : data;
    var img = item && (item.image || item.featured_image);
    if (img && typeof img === 'object') img = img.url || img.src;
    if (typeof img !== 'string') return null;
    return img.indexOf('//') === 0 ? 'https:' + img : img;
  }
  function flyToCart(imageUrl) {
    var cartIcon = document.querySelector('.m-cart-icon-bubble');
    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!imageUrl || !cartIcon || !lastPointer || reduce || !document.body) {
      bumpCartIcon();
      return;
    }
    var startX = lastPointer.x, startY = lastPointer.y;
    var rect = cartIcon.getBoundingClientRect();
    var endX = rect.left + rect.width / 2, endY = rect.top + rect.height / 2;
    var img = document.createElement('img');
    img.src = imageUrl;
    img.className = 'cb-fly-img';
    img.style.left = startX + 'px';
    img.style.top = startY + 'px';
    document.body.appendChild(img);
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        img.style.transform =
          'translate(-50%, -50%) translate(' + (endX - startX) + 'px, ' + (endY - startY) + 'px) scale(0.14)';
        img.style.opacity = '0.35';
      });
    });
    setTimeout(function () {
      if (img.parentNode) img.parentNode.removeChild(img);
      bumpCartIcon();
    }, 720);
  }

  function initCartCount() {
    if (window.MinimogEvents && typeof window.MinimogEvents.subscribe === 'function') {
      window.MinimogEvents.subscribe('cart-update', function (payload) {
        var cart = payload && (payload.cart || payload);
        if (cart && typeof cart.item_count === 'number') updateCartBubble(cart.item_count);
        else getCart().then(function (c) { updateCartBubble(c.item_count); }).catch(function () {});
      });
    }
  }

  /* ====================================================================
     Global cart interceptor — catches EVERY cart mutation (PageFly, apps, the
     theme itself) by wrapping fetch + XHR. After any /cart/add|change|update,
     it re-reads the cart and syncs the header count, the bump animation and all
     cart-boost components — even when nothing opens the drawer. This is what
     makes external add-to-cart (PageFly) reflect live without a page reload.
     ==================================================================== */
  var CART_MUTATION_RE = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;
  var CART_ADD_RE = /\/cart\/add(\.js)?(\?|$)/;
  function handleCartMutation(url, data) {
    setTimeout(syncFromCart, 80);
    if (CART_ADD_RE.test(url)) flyToCart(extractAddedImage(data));
  }
  function syncFromCart() {
    getCart()
      .then(function (cart) {
        updateCartBubble(cart.item_count);
        if (window.MinimogEvents && typeof window.MinimogEvents.emit === 'function') {
          window.MinimogEvents.emit('cart-update', cart);
        }
      })
      .catch(function () {});
  }
  function installCartInterceptor() {
    if (window.fetch && !window.fetch.__cbCartPatched) {
      var origFetch = window.fetch;
      var wrapped = function () {
        var req = arguments[0];
        var url = typeof req === 'string' ? req : (req && req.url) || '';
        var promise = origFetch.apply(this, arguments);
        if (CART_MUTATION_RE.test(url)) {
          var isAdd = CART_ADD_RE.test(url);
          promise.then(function (res) {
            if (res && res.ok === false) return;
            if (isAdd && res && typeof res.clone === 'function') {
              res.clone().json()
                .then(function (data) { handleCartMutation(url, data); })
                .catch(function () { handleCartMutation(url, null); });
            } else {
              handleCartMutation(url, null);
            }
          }).catch(function () {});
        }
        return promise;
      };
      wrapped.__cbCartPatched = true;
      try { window.fetch = wrapped; } catch (e) {}
    }
    if (window.XMLHttpRequest && !XMLHttpRequest.prototype.__cbCartPatched) {
      var origOpen = XMLHttpRequest.prototype.open;
      XMLHttpRequest.prototype.open = function (method, url) {
        this.__cbCartUrl = url;
        return origOpen.apply(this, arguments);
      };
      var origSend = XMLHttpRequest.prototype.send;
      XMLHttpRequest.prototype.send = function () {
        var xhr = this;
        if (xhr.__cbCartUrl && CART_MUTATION_RE.test(xhr.__cbCartUrl)) {
          xhr.addEventListener('load', function () {
            var data = null;
            if (CART_ADD_RE.test(xhr.__cbCartUrl)) {
              try { data = JSON.parse(xhr.responseText); } catch (e) {}
            }
            handleCartMutation(xhr.__cbCartUrl, data);
          });
        }
        return origSend.apply(this, arguments);
      };
      XMLHttpRequest.prototype.__cbCartPatched = true;
    }
  }
  installCartInterceptor();

  function initFooterHelpers() {
    fillDeliveryEstimate();
    initUpsellNav();
    initDrawerRefreshOnOpen();
    initCartCount();
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initFooterHelpers);
  } else {
    initFooterHelpers();
  }
})();
